import { ISpeakerExtended } from "../interfaces/models/IEvent";
import {
  PresenterForm,
  PresenterProfessionalSource,
} from "../hooks/Events/useSessionForm";
import { getGuestSpeakerById } from "../Services/GuestSpeaker";
import { getPersonById } from "../Services/Person";
import { getCompanyById, getCompanies } from "../Services/Company";
import { getFirmById } from "../Services/Firm";
import { resolvePersonDisplayTitle } from "./personDisplayTitle";

function resolveProfessionalSource(
  presenter: PresenterForm,
): PresenterProfessionalSource {
  if (
    presenter.professionalSource === "guestSpeaker" ||
    presenter.personType === "guest"
  ) {
    return "guestSpeaker";
  }
  return "person";
}

async function enrichGuestSpeaker(
  baseSpeaker: ISpeakerExtended,
  presenter: PresenterForm,
): Promise<ISpeakerExtended> {
  let guestSpeaker = null;

  if (!baseSpeaker.photo || !baseSpeaker.title) {
    guestSpeaker = await getGuestSpeakerById(presenter.professional!.id);

    if (guestSpeaker) {
      baseSpeaker.photo =
        baseSpeaker.photo || guestSpeaker.photo || undefined;
      baseSpeaker.title =
        baseSpeaker.title ||
        resolvePersonDisplayTitle(guestSpeaker) ||
        undefined;
      if (!baseSpeaker.name) {
        baseSpeaker.name =
          `${guestSpeaker.firstName || ""} ${guestSpeaker.lastName || ""}`.trim() ||
          undefined;
      }
    }
  }

  if (!baseSpeaker.firmLogo || !baseSpeaker.firmName) {
    if (presenter.memberFirm?.id && presenter.memberFirm.id.trim() !== "") {
      try {
        const company = await getCompanyById(presenter.memberFirm.id);
        if (company) {
          baseSpeaker.firmName =
            baseSpeaker.firmName ||
            company.name ||
            presenter.memberFirm.name ||
            undefined;
          baseSpeaker.firmLogo =
            baseSpeaker.firmLogo || company.logo || undefined;
        }
      } catch (companyError) {
        console.error("Error fetching company by ID:", companyError);
      }
    }

    if (
      !baseSpeaker.firmLogo &&
      (baseSpeaker.firmName || presenter.memberFirm?.name)
    ) {
      const searchName =
        baseSpeaker.firmName ||
        presenter.memberFirm?.name ||
        guestSpeaker?.companyName;
      if (searchName) {
        try {
          const allCompanies = await getCompanies();
          const matchingCompany = allCompanies.find(
            (c) => c.name?.toLowerCase() === searchName.toLowerCase(),
          );
          if (matchingCompany) {
            baseSpeaker.firmName =
              baseSpeaker.firmName || matchingCompany.name || undefined;
            baseSpeaker.firmLogo =
              baseSpeaker.firmLogo || matchingCompany.logo || undefined;
          }
        } catch (companiesError) {
          console.error("Error fetching companies:", companiesError);
        }
      }
    }

    if (!baseSpeaker.firmName) {
      baseSpeaker.firmName =
        guestSpeaker?.companyName || presenter.memberFirm?.name || undefined;
    }
  }

  return baseSpeaker;
}

async function enrichPersonSpeaker(
  baseSpeaker: ISpeakerExtended,
  presenter: PresenterForm,
): Promise<ISpeakerExtended> {
  if (!baseSpeaker.photo || !baseSpeaker.title || !baseSpeaker.firmName) {
    try {
      const person = await getPersonById(presenter.professional!.id);

      if (person) {
        baseSpeaker.photo = baseSpeaker.photo || person.photo || undefined;
        baseSpeaker.title =
          baseSpeaker.title ||
          resolvePersonDisplayTitle(person) ||
          undefined;

        if ((!baseSpeaker.firmName || !baseSpeaker.firmLogo) && person.firm) {
          try {
            const firm = await getFirmById(person.firm);
            baseSpeaker.firmName =
              baseSpeaker.firmName || firm?.name || undefined;
            baseSpeaker.firmLogo =
              baseSpeaker.firmLogo || firm?.logo || undefined;
          } catch (firmError) {
            console.error("Error fetching firm data:", firmError);
          }
        }
      } else {
        return enrichGuestSpeaker(baseSpeaker, presenter);
      }
    } catch (error) {
      console.error("Error fetching speaker/person data:", error);
      return enrichGuestSpeaker(baseSpeaker, presenter);
    }
  }

  if (!baseSpeaker.firmName && presenter.memberFirm?.name) {
    baseSpeaker.firmName = presenter.memberFirm.name;
  }

  return baseSpeaker;
}

export async function buildSessionSpeakers(
  presenters: PresenterForm[],
  existingSpeakers: ISpeakerExtended[] = [],
): Promise<ISpeakerExtended[]> {
  const speakersPromises = presenters
    .filter((p) => p.professional)
    .map(async (p) => {
      const professionalSource = resolveProfessionalSource(p);
      const existingSpeaker = existingSpeakers.find(
        (s) =>
          s.personId === p.professional!.id &&
          s.speakerType === p.personType,
      );

      let baseSpeaker: ISpeakerExtended = existingSpeaker
        ? { ...existingSpeaker, personSource: professionalSource }
        : {
            personId: p.professional!.id,
            speakerType: p.personType,
            name: p.professional!.name,
            firmName: p.memberFirm?.name ?? undefined,
            personSource: professionalSource,
          };

      if (professionalSource === "guestSpeaker") {
        baseSpeaker = await enrichGuestSpeaker(baseSpeaker, p);
      } else {
        baseSpeaker = await enrichPersonSpeaker(baseSpeaker, p);
      }

      return baseSpeaker;
    });

  return Promise.all(speakersPromises);
}
